import React from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   DialogContentText,
   Button,
   Box,
   FormControlLabel,
   Checkbox,
   Tooltip
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import styles from './admin.module.css';

/**
 * Collection of confirmation dialogs used in admin interface
 */
const ConfirmationDialogs = ({
   // Delete user dialog
   deleteConfirmOpen = false,
   userToDelete = '',
   onDeleteConfirmed,
   onDeleteClose,
   
   // Block/unblock user dialog
   blockConfirmOpen = false,
   userToToggleBlock = '',
   blockAction = false,
   onBlockConfirmed,
   onBlockConfirmClose,
   
   // Delete options dialog
   deleteOptionsOpen = false,
   userToDeleteWithOptions = '',
   deleteType = 'soft',
   onDeleteTypeChange,
   onDeleteWithOptionsConfirmed,
   onDeleteOptionsClose,
   
   // Delete field dialog
   deleteFieldConfirmOpen = false,
   fieldToDelete = { email: '', field: '', label: '' },
   resetBirthdayTimer = false,
   onResetBirthdayTimerChange,
   onDeleteField,
   onDeleteFieldConfirmClose,
   deleteProfileFieldLoading = false,
   
   // Reset birthday timer dialog
   resetBirthdayTimerConfirmOpen = false,
   userForBirthdayTimerReset = '',
   onResetBirthdayTimerConfirmed,
   onResetBirthdayTimerConfirmClose,
   resetBirthdayTimerLoading = false
}) => {

   return (
      <>
         {/* Delete User Confirmation Dialog */}
         <Dialog
            open={deleteConfirmOpen}
            onClose={onDeleteClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
         >
            <DialogTitle id="alert-dialog-title">
               {"Confirm User Deletion"}
            </DialogTitle>
            <DialogContent>
               <DialogContentText id="alert-dialog-description">
                  Are you sure you want to delete the user {userToDelete}? This action cannot be undone.
               </DialogContentText>
            </DialogContent>
            <DialogActions>
               <Button onClick={onDeleteClose} color="primary">
                  Cancel
               </Button>
               <Button 
                  onClick={onDeleteConfirmed} 
                  color="error" 
                  variant="contained" 
                  className={styles.dangerButton}
                  autoFocus
               >
                  Delete
               </Button>
            </DialogActions>
         </Dialog>

         {/* Block/Unblock User Confirmation Dialog */}
         <Dialog
            open={blockConfirmOpen}
            onClose={onBlockConfirmClose}
            aria-labelledby="block-dialog-title"
            aria-describedby="block-dialog-description"
         >
            <DialogTitle id="block-dialog-title">
               {`Confirm ${blockAction ? 'Block' : 'Unblock'} Action`}
            </DialogTitle>
            <DialogContent>
               <DialogContentText id="block-dialog-description">
                  {`Are you sure you want to ${blockAction ? 'block' : 'unblock'} the user ${userToToggleBlock}?`}
               </DialogContentText>
            </DialogContent>
            <DialogActions>
               <Button onClick={onBlockConfirmClose} color="primary">
                  Cancel
               </Button>
               <Button 
                  onClick={onBlockConfirmed} 
                  color={blockAction ? 'warning' : 'success'} 
                  variant="contained"
                  className={blockAction ? styles.warningButton : styles.successButton}
                  autoFocus
               >
                  {blockAction ? 'Block' : 'Unblock'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Delete Options Dialog */}
         <Dialog
            open={deleteOptionsOpen}
            onClose={onDeleteOptionsClose}
            aria-labelledby="delete-options-dialog-title"
            aria-describedby="delete-options-dialog-description"
         >
            <DialogTitle id="delete-options-dialog-title">
               Choose Delete Option
            </DialogTitle>
            <DialogContent>
               <DialogContentText id="delete-options-dialog-description" sx={{ mb: 2 }}>
                  Select how you want to delete user {userToDeleteWithOptions}:
               </DialogContentText>
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                     control={
                        <Checkbox
                           checked={deleteType === "soft"}
                           onChange={() => onDeleteTypeChange("soft")}
                           color="primary"
                        />
                     }
                     label="Soft Delete - Mark as deleted in database (can be recovered)"
                  />
                  <FormControlLabel
                     control={
                        <Checkbox
                           checked={deleteType === "hard"}
                           onChange={() => onDeleteTypeChange("hard")}
                           color="primary"
                        />
                     }
                     label="Hard Delete - Permanently remove from database (cannot be recovered)"
                  />
               </Box>
            </DialogContent>
            <DialogActions>
               <Button onClick={onDeleteOptionsClose} color="primary">
                  Cancel
               </Button>
               <Button 
                  onClick={onDeleteWithOptionsConfirmed} 
                  color="error" 
                  variant="contained"
                  className={styles.dangerButton}
                  autoFocus
               >
                  Delete
               </Button>
            </DialogActions>
         </Dialog>

         {/* Delete Profile Field Confirmation Dialog */}
         <Dialog
            open={deleteFieldConfirmOpen}
            onClose={onDeleteFieldConfirmClose}
            aria-labelledby="delete-field-dialog-title"
            aria-describedby="delete-field-dialog-description"
         >
            <DialogTitle id="delete-field-dialog-title">
               Delete {fieldToDelete.label}
            </DialogTitle>
            <DialogContent>
               <DialogContentText id="delete-field-dialog-description">
                  Are you sure you want to delete this user's {fieldToDelete.label.toLowerCase()}? This action cannot be undone.
               </DialogContentText>
               
               {fieldToDelete.field === 'birthday' && (
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                     <FormControlLabel
                        control={
                           <Checkbox
                              checked={resetBirthdayTimer}
                              onChange={onResetBirthdayTimerChange}
                              color="primary"
                           />
                        }
                        label="Reset birthday restriction timer"
                     />
                     <Tooltip title="If checked, the user will be able to update their birthday immediately. Otherwise, they will need to wait until the current restriction period ends.">
                        <InfoIcon color="action" fontSize="small" sx={{ ml: 1, opacity: 0.7 }} />
                     </Tooltip>
                  </Box>
               )}
            </DialogContent>
            <DialogActions>
               <Button 
                  onClick={onDeleteFieldConfirmClose} 
                  color="primary"
                  disabled={deleteProfileFieldLoading}
               >
                  Cancel
               </Button>
               <Button 
                  onClick={onDeleteField} 
                  color="error" 
                  variant="contained"
                  className={styles.dangerButton}
                  disabled={deleteProfileFieldLoading}
               >
                  {deleteProfileFieldLoading ? 'Deleting...' : 'Delete'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Reset Birthday Timer Confirmation Dialog */}
         <Dialog
            open={resetBirthdayTimerConfirmOpen}
            onClose={onResetBirthdayTimerConfirmClose}
            aria-labelledby="reset-birthday-timer-dialog-title"
            aria-describedby="reset-birthday-timer-dialog-description"
         >
            <DialogTitle id="reset-birthday-timer-dialog-title">
               Reset Birthday Restriction Timer
            </DialogTitle>
            <DialogContent>
               <DialogContentText id="reset-birthday-timer-dialog-description">
                  This will allow the user to update their birthday immediately, regardless of when they last changed it.
                  Are you sure you want to reset the birthday restriction timer for {userForBirthdayTimerReset}?
               </DialogContentText>
            </DialogContent>
            <DialogActions>
               <Button 
                  onClick={onResetBirthdayTimerConfirmClose} 
                  color="primary"
                  disabled={resetBirthdayTimerLoading}
               >
                  Cancel
               </Button>
               <Button 
                  onClick={onResetBirthdayTimerConfirmed} 
                  color="primary" 
                  variant="contained"
                  className={styles.primaryButton}
                  disabled={resetBirthdayTimerLoading}
               >
                  {resetBirthdayTimerLoading ? 'Resetting...' : 'Reset Timer'}
               </Button>
            </DialogActions>
         </Dialog>
      </>
   );
};

export default ConfirmationDialogs;
